// useRecorder — React-хук для записи лекции на web + Capacitor.
//
// Phase 4 рефактор: до этого хук был осиротевшим (никто не импортировал) +
// hard-coded к MediaRecorder + использовал apiAny-хак потому что api.recordings
// тогда не было wired. Теперь:
//   - Реальный pipeline через AudioPort (web: MediaRecorder, capacitor: native
//     plugin + foreground service).
//   - recordings.create({ deviceId, language, enableLiveDiarization }) — ПЕРЕД
//     открытием микрофона (multi-device lock + idempotent recordingId).
//   - onChunk → uploadChunk presigned PUT → confirmChunk → live STT триггер.
//   - pause/resume дёргает и платформенный recorder, и backend mutations.
//   - stop → finalize → processRecording Inngest workflow → status='processing'.
//   - Retry-очередь chunk upload'ов с идемпотентностью через (recordingId, sequence)
//     unique index на recording_chunks.
//
// Persistent upload-очередь (2026-07-28): после MAX_CHUNK_RETRY чанк больше
// НЕ выбрасывается молча — кладётся в idb-keyval очередь и доставляется
// фоновым drain'ом (online-событие network port / интервал 30s / recovery
// при старте приложения). stop() ждёт опустошения очереди grace-окном: после
// finalize сервер чанки НЕ принимает (uploadChunk status-guard), поэтому при
// офлайне finalize откладывается (auto-finalize из drain по onQueueEmptied),
// а пользователь видит FinalizePendingBanner с явным «Завершить без них».
// См. lib/recording-upload-queue.ts.
//
// Store sync: `useRecorderStore` обновляется через action setters (status,
// mediaStream для waveform, deviceLabel, chunks counters, recordingId).
//
// Upload-машинерия (одна попытка, retry-цепочка, persistent очередь, finalize
// + deferred auto-finalize) выделена в `hooks/use-chunk-upload-queue.ts`
// (лимит 500 строк на файл) — здесь жизненный цикл записи.

import type { AudioInputState, AudioPort } from "@studyhub/platform/ports/audio.port";
import { useCallback, useEffect, useRef } from "react";
import { uuidv7 } from "uuidv7";
import { CHUNK_SEC, useChunkUploadQueue } from "@/hooks/use-chunk-upload-queue";
import i18n from "@/i18n";
import { logClientError, logClientWarn } from "@/lib/client-log";
import { getDeviceId } from "@/lib/device-id";
import { IS_CAPACITOR } from "@/lib/platform-detect";
import { addPendingFinalizeId, removePendingFinalizeId } from "@/lib/recording-pending-finalize";
import { resolveGenerationLocale } from "@/lib/resolve-generation-locale";
import { api } from "@/lib/trpc-client";
import { useRecorderStore } from "@/stores/recorder";

// ─── Adapter resolution ───────────────────────────────────────────────────
// Web — статический import (всегда нужен в web bundle). Capacitor — dynamic
// import под IS_CAPACITOR гард (rules/capacitor-platform.md: specifier
// через переменную, чтобы Vite не парсил literal).
let cachedAudioPort: AudioPort | null = null;
async function getAudioPort(): Promise<AudioPort> {
	if (cachedAudioPort) return cachedAudioPort;
	if (IS_CAPACITOR) {
		try {
			const moduleId = "@studyhub/platform/adapters/capacitor/audio.capacitor";
			const mod = (await import(moduleId)) as { audioCapacitorAdapter: AudioPort };
			cachedAudioPort = mod.audioCapacitorAdapter;
			return cachedAudioPort;
		} catch (err) {
			logClientWarn("useRecorder: capacitor adapter unavailable, falling back to web", err);
		}
	}
	const { audioWebAdapter } = await import("@studyhub/platform/adapters/web/audio.web");
	cachedAudioPort = audioWebAdapter;
	return cachedAudioPort;
}

// ─── Configuration ────────────────────────────────────────────────────────
/** Grace-окно ожидания очереди при stop(): сеть «мигает» — даём доставить хвост. */
const FINALIZE_DRAIN_GRACE_MS = 20_000;

function sourceFor(isCapacitor: boolean): "web" | "capacitor-android" | "capacitor-ios" {
	if (!isCapacitor) return "web";
	if (typeof navigator !== "undefined" && /iPhone|iPad|iPod/.test(navigator.userAgent)) {
		return "capacitor-ios";
	}
	return "capacitor-android";
}

export interface UseRecorderOptions {
	/** Заголовок записи. Default — «Запись от {date}» формируется на сервере. */
	title?: string;
	/** ISO 639-1 язык STT (для prompt). Default 'ru'. */
	language?: string;
	/** Распознавать спикеров (multi-speaker). Default true. */
	enableLiveDiarization?: boolean;
	/** Опц. привязка к материалу. */
	materialId?: string;
	/** MediaDeviceInfo.deviceId выбранного микрофона (web). */
	inputDeviceId?: string;
}

export interface ContinueRecordingOptions {
	recordingId: string;
	/** Уже загруженные чанки на сервере (0-based next = chunksUploaded при dense). */
	chunksUploaded: number;
	/** Оценка elapsed для таймера UI (мс). */
	elapsedMs: number;
	/** MediaDeviceInfo.deviceId выбранного микрофона (web). */
	inputDeviceId?: string;
}

export interface UseRecorderHandle {
	/** Старт записи. Параметры (title/language/диаризация/material) передаются
	 *  здесь, а не в useRecorder — движок живёт в глобальном провайдере и не
	 *  знает контекста страницы до фактического старта. */
	start: (opts?: UseRecorderOptions) => Promise<void>;
	/**
	 * Продолжить существующую stalled-запись после reload (F5/закрытие вкладки).
	 * Берёт device lock, переводит session в recording, стартует mic с offset
	 * sequence — одна и та же lecture_session, не новая.
	 */
	continueExisting: (opts: ContinueRecordingOptions) => Promise<void>;
	pause: () => Promise<void>;
	resume: () => Promise<void>;
	/** Завершить + триггерить processRecording. Используется кнопкой «Остановить».
	 *  При недоставленной очереди и офлайне finalize ОТКЛАДЫВАЕТСЯ (вернёт null,
	 *  status останется "stopping") — довезёт auto-finalize после drain. */
	stop: () => Promise<{ recordingId: string } | null>;
	/** Принудительный finalize поверх недоставленной очереди (кнопка «Завершить
	 *  без них» в FinalizePendingBanner). Чанки очереди будут отклонены сервером
	 *  и выброшены drain'ом — потеря явная (пользователь подтвердил + toast). */
	forceFinalize: () => Promise<{ recordingId: string } | null>;
	/** Прервать без finalize (soft-delete на сервере). Для «Удалить запись» в exit-confirm. */
	cancel: () => Promise<void>;
	/**
	 * Сменить микрофон во время записи (web). Обновляет MediaStream для
	 * waveform и deviceLabel; sequence/чанки не сбрасываются.
	 */
	switchInputDevice: (deviceId: string) => Promise<void>;
}

export function useRecorder(): UseRecorderHandle {
	const setStatus = useRecorderStore((s) => s.setStatus);
	const setRecordingId = useRecorderStore((s) => s.setRecordingId);
	const setMediaStream = useRecorderStore((s) => s.setMediaStream);
	const setDeviceLabel = useRecorderStore((s) => s.setDeviceLabel);
	const setError = useRecorderStore((s) => s.setError);
	const setChunksUploaded = useRecorderStore((s) => s.setChunksUploaded);
	const setChunksEmitted = useRecorderStore((s) => s.setChunksEmitted);
	const setCaptureStartedAt = useRecorderStore((s) => s.setCaptureStartedAt);
	const incrementChunksEmitted = useRecorderStore((s) => s.incrementChunksEmitted);
	const setElapsedMs = useRecorderStore((s) => s.setElapsedMs);
	const tick = useRecorderStore((s) => s.tick);
	const reset = useRecorderStore((s) => s.reset);
	const recordingId = useRecorderStore((s) => s.recordingId);
	const status = useRecorderStore((s) => s.status);

	const unsubscribeRef = useRef<(() => void) | null>(null);
	const portRef = useRef<AudioPort | null>(null);
	const chunkCountRef = useRef(0);
	/** Offset sequence текущей capture-сессии (0 для новой, N для continue). */
	const startSequenceRef = useRef(0);
	// Upload-машинерия: retry + persistent очередь + finalize (hooks/use-chunk-upload-queue).
	const {
		queue,
		uploadOneChunk,
		finalizeRecording,
		pendingFinalizeRef,
		inFlightUploadsRef,
		undeliverableRecordingsRef,
	} = useChunkUploadQueue({ chunkCountRef });

	const createMutation = api.recordings.create.useMutation();
	const pauseMutation = api.recordings.pause.useMutation();
	const resumeMutation = api.recordings.resume.useMutation();
	const transferLockMutation = api.recordings.transferLock.useMutation();
	const deleteRecMutation = api.recordings.deleteRecording.useMutation();

	const start = useCallback(
		async (opts: UseRecorderOptions = {}) => {
			if (status !== "idle" && status !== "error") return;
			setError(null);
			setStatus("starting");

			try {
				const port = await getAudioPort();
				portRef.current = port;

				const perm = await port.requestPermission();
				if (perm === "denied") {
					setError(i18n.t("recorder.permissionDenied", { ns: "recordings" }));
					setStatus("error");
					return;
				}

				// 1. Create lecture_session на сервере (multi-device lock check).
				const deviceId = getDeviceId();
				const isCapacitor = IS_CAPACITOR;
				const created = await createMutation.mutateAsync({
					title:
						opts.title ??
						i18n.t("recorder.defaultTitle", {
							ns: "recordings",
							date: new Date().toLocaleString(i18n.language),
						}),
					language: opts.language ?? resolveGenerationLocale(),
					source: sourceFor(isCapacitor),
					deviceId,
					enableLiveDiarization: false,
					materialId: opts.materialId,
				});
				setRecordingId(created.recording.id);
				setChunksUploaded(0);
				setChunksEmitted(0);
				setElapsedMs(0);

				// 2. Subscribe ДО старта — чтобы первый chunk не потерялся.
				startSequenceRef.current = 0;
				chunkCountRef.current = 0;
				unsubscribeRef.current = port.onChunk((event) => {
					chunkCountRef.current = event.sequence + 1;
					incrementChunksEmitted();
					void uploadOneChunk(created.recording.id, event);
				});

				// 3. Старт микрофона.
				await port.startRecording({
					chunkSec: CHUNK_SEC,
					bitsPerSecond: 32000,
					inputDeviceId: opts.inputDeviceId,
				});

				// 4. UI state (waveform + шапка).
				const input: AudioInputState = port.getInputState();
				setMediaStream(input.mediaStream);
				setDeviceLabel(input.deviceLabel);
				setCaptureStartedAt(Date.now());
				setStatus("recording");
			} catch (err) {
				logClientError("useRecorder: start failed", err);
				setError(
					err instanceof Error ? err.message : i18n.t("recorder.startFailed", { ns: "recordings" }),
				);
				setStatus("error");
			}
		},
		[
			status,
			setStatus,
			setError,
			setRecordingId,
			setMediaStream,
			setDeviceLabel,
			setChunksUploaded,
			setChunksEmitted,
			setCaptureStartedAt,
			setElapsedMs,
			incrementChunksEmitted,
			createMutation,
			uploadOneChunk,
		],
	);

	const continueExisting = useCallback(
		async (opts: ContinueRecordingOptions) => {
			if (status !== "idle" && status !== "error") return;
			setError(null);
			setStatus("starting");

			const recId = opts.recordingId;
			try {
				const port = await getAudioPort();
				portRef.current = port;

				const perm = await port.requestPermission();
				if (perm === "denied") {
					setError(i18n.t("recorder.permissionDenied", { ns: "recordings" }));
					setStatus("error");
					return;
				}

				const deviceId = getDeviceId();
				// IDB до resume: при сбое очереди не оставляем сессию в recording на сервере.
				const queuedMax = await queue.maxSequenceForRecording(recId);
				const nextSequence = Math.max(opts.chunksUploaded, (queuedMax ?? -1) + 1);

				// Lock на это устройство (после F5 deviceId тот же → same-device;
				// forceTakeover закрывает конфликт с «мёртвым» lock другого таба).
				await transferLockMutation.mutateAsync({
					recordingId: recId,
					newDeviceId: deviceId,
					forceTakeover: true,
				});
				await resumeMutation.mutateAsync({ recordingId: recId });

				// Continue отменяет отложенный finalize этой записи (если был).
				pendingFinalizeRef.current.delete(recId);
				void removePendingFinalizeId(recId);

				startSequenceRef.current = nextSequence;
				chunkCountRef.current = nextSequence;
				setRecordingId(recId);
				setChunksUploaded(opts.chunksUploaded);
				// Считаем только чанки этой capture-сессии — watchdog «тишины»
				// не должен гаснуть из‑за старых uploaded / elapsed прошлой сессии.
				setChunksEmitted(0);
				setElapsedMs(Math.max(0, opts.elapsedMs));

				unsubscribeRef.current?.();
				unsubscribeRef.current = port.onChunk((event) => {
					chunkCountRef.current = event.sequence + 1;
					incrementChunksEmitted();
					void uploadOneChunk(recId, event);
				});

				await port.startRecording({
					chunkSec: CHUNK_SEC,
					bitsPerSecond: 32000,
					startSequence: nextSequence,
					inputDeviceId: opts.inputDeviceId,
				});

				const input: AudioInputState = port.getInputState();
				setMediaStream(input.mediaStream);
				setDeviceLabel(input.deviceLabel);
				setCaptureStartedAt(Date.now());
				setStatus("recording");
				void queue.drain();
			} catch (err) {
				logClientError("useRecorder: continueExisting failed", err);
				setError(
					err instanceof Error
						? err.message
						: i18n.t("recorder.continueFailed", { ns: "recordings" }),
				);
				setStatus("error");
			}
		},
		[
			status,
			setStatus,
			setError,
			setRecordingId,
			setMediaStream,
			setDeviceLabel,
			setChunksUploaded,
			setChunksEmitted,
			setCaptureStartedAt,
			setElapsedMs,
			incrementChunksEmitted,
			transferLockMutation,
			resumeMutation,
			uploadOneChunk,
			queue,
			pendingFinalizeRef,
		],
	);

	const pause = useCallback(async () => {
		if (!recordingId || status !== "recording") return;
		const port = portRef.current;
		if (!port) return;
		await port.pauseRecording();
		setStatus("paused");
		try {
			await pauseMutation.mutateAsync({ recordingId });
		} catch (err) {
			logClientWarn("useRecorder: pause backend call failed (continuing)", err);
		}
	}, [recordingId, status, setStatus, pauseMutation]);

	const resume = useCallback(async () => {
		if (!recordingId || status !== "paused") return;
		const port = portRef.current;
		if (!port) return;
		await port.resumeRecording();
		setStatus("recording");
		try {
			await resumeMutation.mutateAsync({ recordingId });
		} catch (err) {
			logClientWarn("useRecorder: resume backend call failed (continuing)", err);
		}
	}, [recordingId, status, setStatus, resumeMutation]);

	const stop = useCallback(async (): Promise<{ recordingId: string } | null> => {
		const recId = recordingId;
		if (!recId) return null;
		setStatus("stopping");
		const port = portRef.current;
		if (port) {
			const result = await port.stopRecording();
			// Safari quirk: stopRecording() может не fire-нуть ondataavailable на
			// финальном blob. collectedBlobs — локальный индекс с 0 этой capture-
			// сессии; абсолютный sequence = startSequence + localIndex.
			const emittedLocal = chunkCountRef.current - startSequenceRef.current;
			if (result.chunks.length > emittedLocal) {
				for (let i = emittedLocal; i < result.chunks.length; i++) {
					await uploadOneChunk(recId, {
						blob: result.chunks[i] as Blob,
						sequence: startSequenceRef.current + i,
						mimeType: result.mimeType,
					});
				}
			}
		}
		unsubscribeRef.current?.();
		unsubscribeRef.current = null;
		setMediaStream(null);

		// Finalize ЖДЁТ и in-flight ретраи, и очередь: после finalize сервер
		// чанки не принимает (uploadChunk status-guard) — недоставленный хвост
		// потерялся бы навсегда. Бюджет ожидания общий (grace).
		const finalizeDeadline = Date.now() + FINALIZE_DRAIN_GRACE_MS;
		// 1. In-flight ретраи (ещё НЕ в persistent-очереди) — ждём их
		//    завершения: успех, либо enqueue в persistent-очередь.
		while (inFlightUploadsRef.current.size > 0 && Date.now() < finalizeDeadline) {
			// eslint-disable-next-line no-await-in-loop
			await Promise.race([
				Promise.allSettled([...inFlightUploadsRef.current]),
				new Promise<void>((r) => setTimeout(r, 500)),
			]);
		}
		// 2. Теперь всё недоставленное — в persistent-очереди: ждём её.
		const drained =
			inFlightUploadsRef.current.size === 0 &&
			(await queue.drainUntilEmpty(recId, Math.max(0, finalizeDeadline - Date.now())));
		if (!drained || undeliverableRecordingsRef.current.has(recId)) {
			// Гонка с forceFinalize из баннера: запись уже финализирована за время grace.
			if (useRecorderStore.getState().status === "processing") return { recordingId: recId };
			// Офлайн / IDB недоступна: запись остаётся открытой на сервере,
			// очередь доставит хвост при появлении сети → auto-finalize.
			// При undeliverable — только явный forceFinalize.
			pendingFinalizeRef.current.add(recId);
			void addPendingFinalizeId(recId);
			return null;
		}
		return finalizeRecording(recId);
	}, [
		recordingId,
		setStatus,
		setMediaStream,
		uploadOneChunk,
		queue,
		finalizeRecording,
		pendingFinalizeRef,
		inFlightUploadsRef,
		undeliverableRecordingsRef,
	]);

	const forceFinalize = useCallback(async (): Promise<{ recordingId: string } | null> => {
		const recId = recordingId;
		if (!recId) return null;
		pendingFinalizeRef.current.delete(recId);
		void removePendingFinalizeId(recId);
		// Явное «завершить с дырами» — снимаем блок undeliverable.
		undeliverableRecordingsRef.current.delete(recId);
		return finalizeRecording(recId);
	}, [recordingId, finalizeRecording, pendingFinalizeRef, undeliverableRecordingsRef]);

	const cancel = useCallback(async () => {
		const recId = recordingId;
		const port = portRef.current;
		if (port) {
			try {
				await port.stopRecording();
			} catch {
				/* port уже остановлен */
			}
		}
		unsubscribeRef.current?.();
		unsubscribeRef.current = null;
		if (recId) {
			pendingFinalizeRef.current.delete(recId);
			try {
				await deleteRecMutation.mutateAsync({ recordingId: recId });
			} catch (err) {
				logClientWarn("useRecorder: cancel/delete failed (continuing)", err);
			}
			// Запись удалена — чанки очереди ей больше не нужны.
			await queue.dropForRecording(recId);
		}
		reset();
	}, [recordingId, deleteRecMutation, reset, queue, pendingFinalizeRef]);

	// Timer для elapsedMs (1 tick/sec пока recording).
	useEffect(() => {
		if (status !== "recording") return;
		const id = window.setInterval(() => {
			tick(1000);
		}, 1000);
		return () => {
			window.clearInterval(id);
		};
	}, [status, tick]);

	// Фоновая доставка очереди: online-событие network port + интервал +
	// recovery хвоста прошлой сессии (после reload приложения).
	useEffect(() => queue.startBackground(), [queue]);

	// Cleanup на unmount: остановить mic если recording всё ещё active
	// (например пользователь закрыл вкладку без stop).
	useEffect(() => {
		return () => {
			const port = portRef.current;
			if (port) {
				void port.stopRecording().catch(() => {
					/* idempotent */
				});
			}
			unsubscribeRef.current?.();
			unsubscribeRef.current = null;
		};
	}, []);

	const switchInputDevice = useCallback(
		async (deviceId: string) => {
			const port = portRef.current;
			if (!port) return;
			if (status !== "recording" && status !== "paused") return;
			try {
				const input = await port.switchInputDevice(deviceId);
				setMediaStream(input.mediaStream);
				setDeviceLabel(input.deviceLabel);
			} catch (err) {
				logClientWarn("useRecorder: switchInputDevice failed", err);
				setError(
					err instanceof Error
						? err.message
						: i18n.t("recorder.micSource.switchFailed", { ns: "recordings" }),
				);
			}
		},
		[status, setMediaStream, setDeviceLabel, setError],
	);

	return {
		start,
		continueExisting,
		pause,
		resume,
		stop,
		forceFinalize,
		cancel,
		switchInputDevice,
	};
}

// uuidv7 re-export — для созидания idempotent ID'ов на клиенте (например для
// manual-notes optimistic UI до response от mutation).
export { getDeviceId, uuidv7 };
